/*
 * This component handles the source GWP collection functionality.
 * (a collecton user should buy products from to receive gifts)
 * It fetches collection products from the storefront API using GraphQL queries
 * and displays them in a modal.
 */

import * as gqlQueries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import nanoajax from 'nanoajax';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { i18n } from 'lib/i18n.js';
import { handleize, convertGQLToProductJson, formatPrice } from 'utils/general';

export default {
  components: {
    modal: () => import('../modal/modal.vue'),
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'product-card': () => import('../product-card/product-card.vue'),
  },
  data() {
    return {
      gwpCollectionProducts: null,
      isLoading: true,
    };
  },
  async mounted() {
    await this.getGwpCollectionProducts(this.collectionHandle);
  },
  computed: {
    ...mapGetters('global', ['gwpCollectionData']),
    collectionHandle() {
      return this.gwpCollectionData.source_collection;
    },
    loadingText() {
      return this.$t('cart.gwp.loading');
    },
    collectionSelectProductText() {
      return this.$t('cart.gwp.select_collection', {
        collection: this.gwpCollectionData.collection_title,
      });
    },
  },
  methods: {
    async getGwpCollectionProducts(handle) {
      let title = '';
      const data = await storefront.request(gqlQueries.collection, {
        handle,
        first: 100,
      });
      let response = [];
      if (data.collection) {
        response = data.collection.products.edges.map((p) =>
          convertGQLToProductJson(p.node),
        );
      }
      const promises = [];
      if (response.length > 0) {
        for (const e of response) {
          const product = e;
          const inGWP = product.variants.some((variant) => {
            return !!variant.sourceGWPCollectionItem;
          });
          if (inGWP && product.available) {
            if (
              title === e.title.split(window.__GLOBAL__.productDelimiter)[0]
            ) {
              continue;
            }
            const handle = handleize(
              e.title.split(window.__GLOBAL__.productDelimiter)[0],
            );
            const url = `${window.Shopify.routes.root}collections/${handle}?view=collection-shade`;
            title = e.title.split(window.__GLOBAL__.productDelimiter)[0];
            promises.push(
              new Promise((resolve, reject) => {
                nanoajax.ajax({ url }, (code, res) => {
                  if (res.length <= 4) {
                    product.shadeCount = parseInt(res, 10);
                  }
                  resolve(res);
                });
              }),
            );
          }
        }
      }
      await Promise.all(promises);
      if (response) {
        this.isLoading = false;
        const filteredVariants = response.filter((product) => {
          if (
            product.tags.some((tag) =>
              tag.includes('shipping-restricted-replacement|'),
            )
          ) {
            const country = product.title.includes(
              window.__GLOBAL__.productDelimiter,
            )
              ? product.title
                  .split(window.__GLOBAL__.productDelimiter)[1]
                  .replace(/[^A-Z]+/g, '')
              : product.variants[0].title;

            const shippingCode = window.shippingCode.replace(/[^A-Z]+/g, '');

            return (
              country === shippingCode ||
              (country === 'US' && shippingCode === 'CA')
            );
          }
          return true;
        });

        const filteredGWP = filteredVariants
          .filter((p) => p && p.variants.length)
          .reduce((unique, o) => {
            const title = o.title.split(window.__GLOBAL__.productDelimiter)[0];
            if (!unique.some((obj) => obj.title.includes(title))) {
              unique.push(o);
            }
            return unique;
          }, []);

        this.gwpCollectionProducts = filteredGWP;
      } else {
        console.warn('There was an error receiving the products');
      }
    },
    async checkReplacementProduct(product) {
      const restrictedProductTag = product.tags?.find((tag) =>
        tag.includes(`shipping-restricted-replacement|${window.shippingCode}`),
      );
      if (restrictedProductTag) {
        const restrictedProductId = restrictedProductTag.split(
          `shipping-restricted-replacement|${window.shippingCode}|`,
        )[1];
        const restrictedProduct = await this.getRestrictedProductById(
          restrictedProductId,
        );
        if (!restrictedProduct.node) {
          console.warn('No replacement for the restricted product was found');
          return false;
        }
      }
      return true;
    },
    async getRestrictedProductById(id) {
      const response = await storefront.request(
        gqlQueries.productAvailabilityByVariantId,
        {
          id: 'gid://shopify/ProductVariant/' + id,
        },
      );
      return response;
    },
  },
};
